import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  FiservCardInterface,
  FiservCardStatusEnum,
  Inscription,
  Preloader,
  SelectDropdown,
  Text,
  WarningCard,
  WarningIcon,
} from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';

import { CARD_LOCKED_REASONS_OPTIONS } from '../../Cards.constants';

import useCardAccessModalStore from './stores/useCardAccessModal.store';
import { CardAccessListItem } from './CardAccessListItem';

interface Props {
  card?: FiservCardInterface;
  lockedPursesIds: string[];
  isCardLocked?: boolean;
  isLoading?: boolean;
  onCancel: () => void;
  onChangeStatusMutationHandler: (status: FiservCardStatusEnum, lockedPurseIds?: string[]) => void;
}

export const CardAccessConfirmationModal: React.FC<Props> = ({
  card,
  lockedPursesIds,
  isCardLocked,
  isLoading,
  onCancel,
  onChangeStatusMutationHandler,
}) => {
  const [showError, setShowError] = useState(false);
  const { handleSetLockReason, state: { lockReason }} = useCardAccessModalStore();
  
  const activePurses = useMemo(() => {
    return card?.cardPurses?.filter((purse) => !lockedPursesIds.includes(purse.id)) || [];
  }, [card?.cardPurses, lockedPursesIds]);

  const lockedPurses = useMemo(() => {
    return card?.cardPurses?.filter((purse) => lockedPursesIds.includes(purse.id)) || [];
  }, [card?.cardPurses, lockedPursesIds]);

  const handleConfirmClick = useCallback(() => {
    if (!lockReason) {
      setShowError(true);
      return;
    }
    setShowError(false);
    onChangeStatusMutationHandler(
      isCardLocked ? FiservCardStatusEnum.FROZEN : FiservCardStatusEnum.ACTIVE,
      lockedPurses.map((purse) => purse.id)
    );
  }, [isCardLocked, lockReason, lockedPurses, onChangeStatusMutationHandler]);

  return (
    <Box direction="column">
      <Box round="moduleRound" pad="l" background="module">
        <Box>
          <Text color="textBody" weight="bold" margin={{ top: 'xs', bottom: 'xs' }}>{isCardLocked ? 'Card to be locked:' : 'Card to be unlocked:'}</Text>
          <CardAccessListItem
            card={card}
            withoutToggle
          />
          {
            activePurses?.length ? (
              <>
                <Text color="textBody" weight="bold" margin={{ top: 'xl', bottom: 'xs' }}>Accounts to be unlocked:</Text>
                {
                  activePurses?.map((purse) => (
                    <CardAccessListItem
                      key={purse.id}
                      purse={purse}
                      withoutToggle
                    />
                  ))
                }
              </>
            ) : null
          }
          {
            lockedPurses?.length ? (
              <>
                <Text color="textBody" weight="bold" margin={{ top: 'xl', bottom: 'xs' }}>Accounts to be locked:</Text>
                {
                  lockedPurses?.map((purse) => (
                    <CardAccessListItem
                      key={purse.id}
                      purse={purse}
                      withoutToggle
                    />
                  ))
                }
              </>
            ) : null
          }
          <Box
            direction="row"
            align="center"
            justify="between"
            background="canvas"
            height="88px"
            pad="l"
            round="container1Round"
            border={{ color: 'border2', size: 'small' }}
            margin={{ top: 'xs' }}
          >
            <Text size="14px" color="textBody" weight={700}>
              Lock reason
            </Text>
            <Box width="320px" margin={{ left: 'xs' }}>
              <SelectDropdown
                id="lockedReason"
                testId="locked-reason"
                name="lockedReason"
                placeholder="Select lock reason"
                singleMode
                options={CARD_LOCKED_REASONS_OPTIONS}
                value={lockReason}
                errorText={showError && !lockReason ? 'This field is required' : undefined}
                onChange={handleSetLockReason}
              />
            </Box>
          </Box>
          <Box margin={{ top: 'xs' }}>
            <WarningCard
              icon={<WarningIcon />}
              title={<Inscription weight="bold" lineHeight="20px" color="textBody">The account(s) selected will be locked for the primary card as well as any dependent cards.</Inscription>}
              topBorderColor="transparent"
              containerProps={{ height: '72px', pad: { horizontal: 'l', vertical: 'm' }, round: 'container2Round' }}
            />
          </Box>
        </Box>
      </Box>
      <Box
        direction="row"
        fill="horizontal"
        margin={{ top: 'l' }}
        align="end"
        justify="end"
        width="medium"
        gap="xs"
      >
        <AppButton
          buttonType="secondary"
          width="140px"
          type="button"
          onClick={onCancel}
        >
          Cancel
        </AppButton>
        <AppButton
          disabled={isLoading}
          type="button"
          onClick={handleConfirmClick}
          width="140px"
          color="blue"
        >
          {isLoading ? <Preloader color="white" /> : 'Confirm'}
        </AppButton>
      </Box>
    </Box>
  );
};
